import { computed, ComputedRef, Ref, watchEffect } from 'vue'

const PAGE_SIZE = 10
const MAX_VISIBLE_PAGES = 5

export type UsePaginationReturnType = {
  totalPages: ComputedRef<number>
  nextPage: ComputedRef<number | null>
  prevPage: ComputedRef<number | null>
  visiblePages: ComputedRef<number[]>
}

export default function usePagination(
  page: Ref<number>,
  totalResults: Ref<number>,
  pageSize: Ref<number>,
  onPageChangeCallback: (newPage: number) => void,
): UsePaginationReturnType {
  const totalPages = computed(() =>
    Math.ceil(totalResults.value / pageSize.value || PAGE_SIZE),
  )
  const nextPage = computed(() => {
    return page.value === totalPages.value || totalResults.value === 0
      ? null
      : page.value + 1
  })
  const prevPage = computed(() =>
    page.value === 0 || totalResults.value === 0 ? null : page.value - 1,
  )
  const visiblePages = computed(() => {
    if (totalResults.value === 0) {
      return []
    }

    const pagePosition = Math.ceil(page.value / MAX_VISIBLE_PAGES)
    const lastPagePosition = Math.ceil(totalPages.value / MAX_VISIBLE_PAGES)
    const firstVisiblePage =
      pagePosition * MAX_VISIBLE_PAGES - MAX_VISIBLE_PAGES
    const isLastPage = pagePosition === lastPagePosition
    const pageRemainder = totalPages.value % MAX_VISIBLE_PAGES
    const visiblePagesCount = isLastPage
      ? pageRemainder || MAX_VISIBLE_PAGES
      : MAX_VISIBLE_PAGES

    return Array.from(new Array(visiblePagesCount)).map((_, index) => {
      return firstVisiblePage + index + 1
    })
  })
  watchEffect(() => {
    // if page falls out of bounds , we arbitrarily set page to last available value
    if (page.value > totalPages.value && totalPages.value > 0) {
      onPageChangeCallback(totalPages.value)
    }
  })
  return {
    totalPages,
    nextPage,
    prevPage,
    visiblePages,
  }
}
